import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { ZoomMtg } from "@zoom/meetingsdk";
import CryptoJS from "crypto-js";
import "./zoom_component.scss";

const Zoom = ({ userData, liveStreamData }) => {
  const [zoomState, setZoomState] = useState({
    isInitialized: false,
    isJoining: false,
    error: null,
  });

  // Stop Microsoft Clarity when Zoom loads
  useEffect(() => {
    if (window.clarity) {
      window.clarity("stop");
    }
  }, []);

  const setError = (message) =>
    setZoomState((prev) => ({ ...prev, error: message }));

  const decrypt = (value) => {
    try {
      if (!value) return null;

      const encrypted = JSON.parse(atob(value));
      const key = CryptoJS.enc.Base64.parse(
        "0CiuKpLCZbX7gBCyYTka4USxTnh7ssXKk8bJ6Y3TXPc="
      );

      const decrypted = CryptoJS.AES.decrypt(encrypted.value, key, {
        iv: CryptoJS.enc.Base64.parse(encrypted.iv),
      });

      return decrypted.toString(CryptoJS.enc.Utf8).split("*")[1];
    } catch {
      return null;
    }
  };

  const generateSignature = (
    meetingNumber,
    role,
    sdkKey,
    sdkSecret,
    iat,
    exp
  ) => {
    try {
      const payload = {
        sdkKey,
        mn: meetingNumber,
        role,
        iat,
        exp,
        appKey: sdkKey,
        tokenExp: exp,
      };

      const encodedHeader = btoa(
        JSON.stringify({ alg: "HS256", typ: "JWT" })
      );
      const encodedPayload = btoa(JSON.stringify(payload));

      const signature = CryptoJS.HmacSHA256(
        `${encodedHeader}.${encodedPayload}`,
        sdkSecret
      )
        .toString(CryptoJS.enc.Base64)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");

      return `${encodedHeader}.${encodedPayload}.${signature}`;
    } catch {
      return null;
    }
  };

  const handleJoinMeeting = useCallback(() => {
    if (!zoomState.isInitialized || zoomState.isJoining) return;

    if (!liveStreamData) {
      setError("Meeting data not available");
      return;
    }

    setZoomState((prev) => ({ ...prev, isJoining: true }));

    const now = Math.floor(Date.now() / 1000);
    const iat = now - 30;
    const exp = iat + 60 * 60 * 2;

    try {
      const meetingNumber = decrypt(liveStreamData.zoom_id);
      const password = decrypt(liveStreamData.password);
      const sdkKey = decrypt(liveStreamData.sdk_key);
      const sdkSecret = decrypt(liveStreamData.sdk_secret_key);
      const userEmail = decrypt(liveStreamData.zoom_email);

      if (!meetingNumber || !sdkKey || !sdkSecret) {
        setError("Invalid meeting configuration");
        return;
      }

      const signature = generateSignature(
        meetingNumber,
        0,
        sdkKey,
        sdkSecret,
        iat,
        exp
      );

      const userName = `${userData?.roll_no || "User"}-${
        userData?.name || "Guest"
      }`;

      ZoomMtg.join({
        meetingNumber,
        signature,
        sdkKey,
        userName,
        userEmail,
        passWord: password,
        success: () => {
          setZoomState((prev) => ({
            ...prev,
            isJoining: false,
            error: null,
          }));
        },
        error: () => {
          setError("Failed to join meeting");
        },
      });
    } catch {
      setError("Unexpected error");
    }
  }, [zoomState.isInitialized, zoomState.isJoining, liveStreamData, userData]);

  useEffect(() => {
    const initializeZoom = async () => {
      try {
        ZoomMtg.setZoomJSLib(
          "https://source.zoom.us/3.13.2/lib",
          "/av"
        );

        ZoomMtg.preLoadWasm();
        ZoomMtg.prepareWebSDK();

        await ZoomMtg.init({
          leaveUrl: "https://learning.motion.ac.in/user_dashboard",
          isSupportAV: true,
          success: () => {
            setZoomState((prev) => ({
              ...prev,
              isInitialized: true,
            }));
          },
          error: () => {
            setError("Zoom initialization failed");
          },
        });
      } catch {
        setError("Zoom initialization failed");
      }
    };

    initializeZoom();
  }, []);

  useEffect(() => {
    if (zoomState.isInitialized && liveStreamData) {
      handleJoinMeeting();
    }
  }, [zoomState.isInitialized, liveStreamData, handleJoinMeeting]);

  return (
    <>
      {zoomState.error && (
        <div className="error-toast">{zoomState.error}</div>
      )}
      <div id="meetingSDKElement" data-clarity-mask="true">
        <div id="zmmtg-root"></div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  liveStreamData: state.userActivity.liveStreamData,
  userData: state.auth.user,
});

export default connect(mapStateToProps)(Zoom);
